// UtilityHitPage.js v2.0 — 5-step guided utility strike reporting wizard
// Hub integration: accepts ?project=X&from=hub URL params, auto-captures GPS, Spanish support
// Captures all 11 Highline/Aspen mandatory fields, photos, generates PDF, emails to claims
import React, { useState, useEffect } from 'react';
import {
  ArrowLeft, ArrowRight, AlertTriangle, MapPin, Camera, CheckCircle,
  Shield, FileText, Navigation, Loader, Send, Phone, X, Eye, Globe
} from 'lucide-react';
import { colors, UTILITY_HIT_CONFIG } from '../config/constants';
import { createUtilityHitPdf } from '../services/pdfService';
import { useTranslation } from '../config/translations';
import { useMobile } from '../utils/theme';

const UtilityHitPage = ({ darkMode, setCurrentPage, loggedInUser }) => {
  const { t, lang, toggleLang } = useTranslation();
  const isMobile = useMobile();

  const accentPrimary = darkMode ? '#667eea' : '#00b4d8';
  const accentSecondary = darkMode ? '#ff6b35' : '#28a745';
  const bgColor = darkMode ? colors.dark : '#f8fafc';
  const cardBg = darkMode ? colors.darkLight : '#ffffff';
  const textColor = darkMode ? '#ffffff' : '#1e293b';
  const mutedColor = darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280';
  const borderColor = darkMode ? '#374151' : '#e5e7eb';
  const dangerColor = '#ef4444';

  // Parse URL params
  const getUrlParams = () => {
    try {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.split('?')[1] || '');
      return {
        project: params.get('project') || null,
        from: params.get('from') || null,
      };
    } catch { return { project: null, from: null }; }
  };
  const { project, from } = getUrlParams();

  const STEPS = [
    { num: 1, label: t('uh_emergency'), icon: Shield },
    { num: 2, label: t('uh_incident'), icon: MapPin },
    { num: 3, label: t('uh_compliance'), icon: FileText },
    { num: 4, label: t('uh_photos'), icon: Camera },
    { num: 5, label: t('uh_review'), icon: CheckCircle },
  ];

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submittedHitId, setSubmittedHitId] = useState(null);

  // Step 1 — Emergency checklist
  const checklistItems = [t('uh_checklist_1'), t('uh_checklist_2'), t('uh_checklist_3'), t('uh_checklist_4')];
  const [checklist, setChecklist] = useState(UTILITY_HIT_CONFIG.emergencyChecklist.map(() => false));
  const [calledWho, setCalledWho] = useState('');

  // Step 2 — Incident details
  const now = new Date();
  const [incidentDate, setIncidentDate] = useState(now.toISOString().slice(0, 16));
  const [address, setAddress] = useState('');
  const [gpsLat, setGpsLat] = useState(null);
  const [gpsLng, setGpsLng] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [utilityType, setUtilityType] = useState('');
  const [utilityOwner, setUtilityOwner] = useState('');
  const [severity, setSeverity] = useState('');
  const [description, setDescription] = useState('');

  // Step 3 — Compliance
  const [markedProperly, setMarkedProperly] = useState('');
  const [locatesCalled, setLocatesCalled] = useState('');
  const [locateTicket, setLocateTicket] = useState('');
  const [crewLeadName, setCrewLeadName] = useState('');
  const [crewLeadPhone, setCrewLeadPhone] = useState('');
  const [equipmentUsed, setEquipmentUsed] = useState('');
  const [immediateActions, setImmediateActions] = useState('');
  const [utilityRepName, setUtilityRepName] = useState('');
  const [utilityRepContact, setUtilityRepContact] = useState('');

  // Step 4 — Photos
  const [photosPre, setPhotosPre] = useState([]);
  const [photosDamage, setPhotosDamage] = useState([]);
  const [photosRepair, setPhotosRepair] = useState([]);

  // Auto-capture GPS on mount when coming from hub
  useEffect(() => {
    if (from === 'hub' && navigator.geolocation) {
      setGpsLoading(true);
      navigator.geolocation.getCurrentPosition(
        (pos) => { setGpsLat(pos.coords.latitude); setGpsLng(pos.coords.longitude); setGpsLoading(false); },
        () => { setGpsLoading(false); },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
  }, [from]);

  const captureGps = () => {
    if (!navigator.geolocation) {
      showMsg('error', 'GPS not available');
      return;
    }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setGpsLat(pos.coords.latitude); setGpsLng(pos.coords.longitude); setGpsLoading(false); showMsg('success', t('uh_gps_captured')); },
      () => { setGpsLoading(false); showMsg('error', 'Could not get GPS location'); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const showMsg = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3000);
  };

  const handlePhotoAdd = (setter, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setter(prev => [...prev, { base64: reader.result.split(',')[1], name: file.name, preview: reader.result }]);
    };
    reader.readAsDataURL(file);
  };

  const removePhoto = (setter, idx) => {
    setter(prev => prev.filter((_, i) => i !== idx));
  };

  // Validation per step
  const canProceed = () => {
    switch (step) {
      case 1: return checklist.every(Boolean);
      case 2: return address.trim() && utilityType && severity && description.trim();
      case 3: return markedProperly && locatesCalled && (locatesCalled === 'No' || locateTicket.trim());
      case 4: return photosDamage.length >= 1;
      case 5: return true;
      default: return true;
    }
  };

  // Build data object
  const buildData = () => ({
    incident_date: incidentDate,
    address: address.trim(),
    gps_lat: gpsLat,
    gps_lng: gpsLng,
    utility_type: utilityType,
    utility_owner: utilityOwner.trim(),
    severity,
    description: description.trim(),
    marked_properly: markedProperly,
    locates_called: locatesCalled,
    locate_ticket_number: locateTicket.trim(),
    crew_lead_name: crewLeadName.trim(),
    crew_lead_phone: crewLeadPhone.trim(),
    equipment_used: equipmentUsed.trim(),
    immediate_actions: immediateActions.trim(),
    utility_rep_name: utilityRepName.trim(),
    utility_rep_contact: utilityRepContact.trim(),
    reported_by: loggedInUser?.name || 'Unknown',
    reported_by_email: loggedInUser?.email || '',
  });

  // Preview PDF
  const handlePreviewPdf = async () => {
    try {
      const data = buildData();
      const pdfBytes = await createUtilityHitPdf(data);
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      showMsg('error', 'Failed to generate PDF preview');
    }
  };

  // Submit
  const handleSubmit = async () => {
    setLoading(true);
    try {
      const data = buildData();

      // 1. Create the hit record
      const resp = await fetch('/api/utility-hits', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const result = await resp.json();
      if (!result.success) throw new Error(result.error || 'Failed to save report');

      const hitId = result.data.id;

      // 2. Upload photos by category
      const uploadPhotos = async (photos, category) => {
        for (const photo of photos) {
          await fetch('/api/utility-hit-photos', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              hit_id: hitId,
              category,
              file_name: photo.name,
              content_type: 'image/jpeg',
              base64: photo.base64,
            }),
          });
        }
      };

      if (photosPre.length) await uploadPhotos(photosPre, 'pre');
      if (photosDamage.length) await uploadPhotos(photosDamage, 'damage');
      if (photosRepair.length) await uploadPhotos(photosRepair, 'repair');

      // 3. Generate PDF
      const pdfBytes = await createUtilityHitPdf(data);
      const pdfBase64 = btoa(String.fromCharCode(...new Uint8Array(pdfBytes)));

      // 4. Email via submit endpoint
      await fetch('/api/utility-hit-submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hit_id: hitId, pdf_base64: pdfBase64 }),
      });

      setSubmitted(true);
      setSubmittedHitId(hitId);
    } catch (err) {
      showMsg('error', err.message || 'Failed to submit report');
    }
    setLoading(false);
  };

  const goBack = () => {
    if (from === 'hub') {
      window.location.hash = `#field-hub?project=${project || 'BASTROP'}`;
    } else {
      setCurrentPage('admin-dashboard');
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '12px',
    border: `1px solid ${borderColor}`,
    borderRadius: '8px',
    backgroundColor: darkMode ? '#1a2332' : '#ffffff',
    color: textColor,
    fontSize: '0.95rem',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const selectStyle = { ...inputStyle, appearance: 'auto' };
  const labelStyle = { display: 'block', marginBottom: '6px', color: mutedColor, fontSize: '0.85rem', fontWeight: '500' };

  // Translated severity descriptions
  const severityDescs = {
    minor: t('uh_sev_minor'),
    moderate: t('uh_sev_moderate'),
    major: t('uh_sev_major'),
    critical: t('uh_sev_critical'),
  };

  // Success screen
  if (submitted) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: bgColor, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
        <div style={{ backgroundColor: cardBg, borderRadius: '16px', padding: '40px', border: `1px solid ${borderColor}`, maxWidth: '500px', width: '100%', textAlign: 'center' }}>
          <CheckCircle size={60} style={{ color: accentSecondary, marginBottom: '16px' }} />
          <h2 style={{ color: textColor, fontSize: '1.4rem', fontWeight: '700', margin: '0 0 8px' }}>{t('uh_submitted')}</h2>
          <p style={{ color: mutedColor, fontSize: '0.95rem', margin: '0 0 24px' }}>
            {t('uh_submitted_desc')}
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            {from === 'hub' && (
              <button
                onClick={() => { window.location.hash = `#field-hub?project=${project || 'BASTROP'}`; }}
                style={{ padding: '12px 24px', backgroundColor: accentPrimary, color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.95rem', fontWeight: '600' }}
              >
                {t('uh_back_to_hub')}
              </button>
            )}
            <button
              onClick={() => setCurrentPage('utility-hit-list')}
              style={{ padding: '12px 24px', backgroundColor: from === 'hub' ? 'transparent' : accentPrimary, color: from === 'hub' ? textColor : '#fff', border: from === 'hub' ? `1px solid ${borderColor}` : 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.95rem', fontWeight: '600' }}
            >
              {t('uh_view_all')}
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{ padding: '12px 24px', backgroundColor: 'transparent', color: textColor, border: `1px solid ${borderColor}`, borderRadius: '8px', cursor: 'pointer', fontSize: '0.95rem' }}
            >
              {t('uh_submit_another')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step progress bar
  const renderStepBar = () => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', marginBottom: '24px', flexWrap: 'wrap' }}>
      {STEPS.map((s, i) => {
        const Icon = s.icon;
        const isActive = step === s.num;
        const isDone = step > s.num;
        return (
          <React.Fragment key={s.num}>
            {i > 0 && <div style={{ width: '24px', height: '2px', backgroundColor: isDone ? accentSecondary : borderColor }} />}
            <div
              onClick={() => { if (isDone) setStep(s.num); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '6px 12px', borderRadius: '20px',
                backgroundColor: isActive ? accentPrimary + '20' : isDone ? accentSecondary + '15' : 'transparent',
                border: `1px solid ${isActive ? accentPrimary : isDone ? accentSecondary : borderColor}`,
                cursor: isDone ? 'pointer' : 'default',
              }}
            >
              <Icon size={14} style={{ color: isActive ? accentPrimary : isDone ? accentSecondary : mutedColor }} />
              {!isMobile && (
                <span style={{ fontSize: '0.75rem', fontWeight: isActive ? '600' : '400', color: isActive ? accentPrimary : isDone ? accentSecondary : mutedColor }}>
                  {s.label}
                </span>
              )}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );

  // Photo upload section
  const renderPhotoSection = (label, photos, setter, required) => (
    <div style={{ marginBottom: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <label style={{ color: textColor, fontSize: '0.9rem', fontWeight: '600' }}>
          {label} {required && <span style={{ color: dangerColor }}>*</span>}
        </label>
        <label style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          padding: '6px 14px', backgroundColor: 'transparent', color: accentPrimary,
          border: `1px solid ${accentPrimary}`, borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem',
        }}>
          <Camera size={14} /> {t('uh_add_photo')}
          <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={(e) => handlePhotoAdd(setter, e.target.files[0])} />
        </label>
      </div>
      {photos.length > 0 ? (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {photos.map((p, i) => (
            <div key={i} style={{ position: 'relative', width: '90px', height: '90px' }}>
              <img src={p.preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px', border: `1px solid ${borderColor}` }} />
              <button
                onClick={() => removePhoto(setter, i)}
                style={{
                  position: 'absolute', top: '-6px', right: '-6px',
                  width: '22px', height: '22px', borderRadius: '50%',
                  backgroundColor: dangerColor, color: '#fff', border: 'none',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                }}
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ padding: '24px', border: `2px dashed ${borderColor}`, borderRadius: '8px', textAlign: 'center', color: mutedColor, fontSize: '0.85rem' }}>
          {t('uh_no_photos')}
        </div>
      )}
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', backgroundColor: bgColor, padding: '24px 16px' }}>
      {/* Toast */}
      {message && (
        <div style={{
          position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)',
          padding: '12px 24px', backgroundColor: message.type === 'success' ? accentSecondary : dangerColor,
          color: '#fff', borderRadius: '10px', zIndex: 1000, fontSize: '0.9rem', fontWeight: '500',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)', maxWidth: '90vw', textAlign: 'center',
        }}>
          {message.text}
        </div>
      )}

      <div style={{ maxWidth: '700px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <button onClick={goBack} style={{
            display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px',
            backgroundColor: 'transparent', border: `1px solid ${borderColor}`,
            borderRadius: '8px', color: textColor, cursor: 'pointer', fontSize: '0.9rem',
          }}>
            <ArrowLeft size={18} /> {from === 'hub' ? t('uh_back_to_hub') : t('uh_back')}
          </button>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: '1.3rem', fontWeight: '700', color: dangerColor, margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <AlertTriangle size={22} />
              {t('uh_title')}
            </h1>
            <p style={{ color: mutedColor, margin: '2px 0 0', fontSize: '0.8rem' }}>
              {t('uh_step')} {step} {t('uh_of')} 5
            </p>
          </div>
          <button onClick={toggleLang} style={{
            display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 14px',
            backgroundColor: darkMode ? '#1e293b' : '#ffffff', border: `1px solid ${borderColor}`,
            borderRadius: '20px', color: textColor, cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600',
          }}>
            <Globe size={14} /> {lang === 'en' ? 'ES' : 'EN'}
          </button>
        </div>

        {renderStepBar()}

        {/* STEP 1 — Emergency Checklist */}
        {step === 1 && (
          <div style={{ backgroundColor: cardBg, borderRadius: '12px', padding: '24px', border: `2px solid ${dangerColor}` }}>
            <h2 style={{ color: dangerColor, fontSize: '1.2rem', fontWeight: '700', margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Shield size={20} /> {t('uh_emergency')}
            </h2>
            <p style={{ color: mutedColor, fontSize: '0.85rem', margin: '0 0 20px' }}>
              {t('uh_emergency_desc')}
            </p>

            {checklistItems.map((item, i) => (
              <div
                key={i}
                onClick={() => setChecklist(prev => prev.map((v, j) => j === i ? !v : v))}
                style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  padding: '14px 16px', marginBottom: '8px',
                  backgroundColor: checklist[i] ? (darkMode ? '#1a3a2a' : '#f0fdf4') : (darkMode ? '#2a1a1a' : '#fef2f2'),
                  borderRadius: '8px', cursor: 'pointer',
                  border: `1px solid ${checklist[i] ? accentSecondary + '40' : dangerColor + '40'}`,
                }}
              >
                <div style={{
                  width: '24px', height: '24px', borderRadius: '6px', flexShrink: 0,
                  border: `2px solid ${checklist[i] ? accentSecondary : borderColor}`,
                  backgroundColor: checklist[i] ? accentSecondary : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {checklist[i] && <CheckCircle size={14} color="#fff" />}
                </div>
                <span style={{ fontSize: '0.95rem', color: textColor, fontWeight: '500' }}>{item}</span>
              </div>
            ))}

            <div style={{ marginTop: '16px' }}>
              <label style={labelStyle}>{t('uh_called_who')}</label>
              <input type="text" value={calledWho} onChange={(e) => setCalledWho(e.target.value)} placeholder="e.g. Atmos Energy, 811" style={inputStyle} />
            </div>

            {!checklist.every(Boolean) && (
              <div style={{ marginTop: '16px', padding: '12px', backgroundColor: dangerColor + '15', borderRadius: '8px', color: dangerColor, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <AlertTriangle size={16} /> {t('uh_check_all')}
              </div>
            )}
          </div>
        )}

        {/* STEP 2 — Incident Details */}
        {step === 2 && (
          <div style={{ backgroundColor: cardBg, borderRadius: '12px', padding: '24px', border: `1px solid ${borderColor}` }}>
            <h2 style={{ color: textColor, fontSize: '1.1rem', fontWeight: '700', margin: '0 0 20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <MapPin size={20} style={{ color: accentPrimary }} /> {t('uh_incident')}
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={labelStyle}>{t('uh_date_time')} *</label>
                <input type="datetime-local" value={incidentDate} onChange={(e) => setIncidentDate(e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>{t('uh_utility_type')} *</label>
                <select value={utilityType} onChange={(e) => setUtilityType(e.target.value)} style={selectStyle}>
                  <option value="">{t('uh_select_type')}</option>
                  {UTILITY_HIT_CONFIG.utilityTypes.map(ut => <option key={ut} value={ut}>{ut}</option>)}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>{t('uh_address')} *</label>
              <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} placeholder={lang === 'es' ? 'Direccion o interseccion' : 'Street address or intersection'} style={inputStyle} />
            </div>

            <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <button onClick={captureGps} disabled={gpsLoading} style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '8px 16px', backgroundColor: gpsLat ? accentSecondary : 'transparent',
                border: `1px solid ${gpsLat ? accentSecondary : borderColor}`,
                borderRadius: '8px', color: gpsLat ? '#fff' : textColor,
                cursor: gpsLoading ? 'not-allowed' : 'pointer', fontSize: '0.85rem',
              }}>
                {gpsLoading ? <Loader size={14} className="spin" /> : <Navigation size={14} />}
                {gpsLat ? t('uh_gps_captured') : t('uh_capture_gps')}
              </button>
              {gpsLat && <span style={{ fontSize: '0.8rem', color: mutedColor }}>{gpsLat.toFixed(5)}, {gpsLng.toFixed(5)}</span>}
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>{t('uh_utility_owner')}</label>
              <input type="text" value={utilityOwner} onChange={(e) => setUtilityOwner(e.target.value)} placeholder={lang === 'es' ? 'Ej: Atmos Energy, Oncor' : 'e.g. Atmos Energy, Oncor, City of Bastrop'} style={inputStyle} />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>{t('uh_severity')} *</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '8px' }}>
                {UTILITY_HIT_CONFIG.severityLevels.map(s => (
                  <div
                    key={s.value}
                    onClick={() => setSeverity(s.value)}
                    style={{
                      padding: '12px', borderRadius: '8px', cursor: 'pointer', textAlign: 'center',
                      border: `2px solid ${severity === s.value ? s.color : borderColor}`,
                      backgroundColor: severity === s.value ? s.color + '15' : 'transparent',
                    }}
                  >
                    <div style={{ fontSize: '0.9rem', fontWeight: '600', color: s.color }}>{s.label}</div>
                    <div style={{ fontSize: '0.7rem', color: mutedColor, marginTop: '4px' }}>{severityDescs[s.value] || s.description}</div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label style={labelStyle}>{t('uh_description')} *</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder={lang === 'es' ? 'Describa lo que paso y el dano' : 'Describe what happened, what was damaged, and the extent of the damage'} rows={4} style={{ ...inputStyle, resize: 'vertical' }} />
            </div>
          </div>
        )}

        {/* STEP 3 — Compliance */}
        {step === 3 && (
          <div style={{ backgroundColor: cardBg, borderRadius: '12px', padding: '24px', border: `1px solid ${borderColor}` }}>
            <h2 style={{ color: textColor, fontSize: '1.1rem', fontWeight: '700', margin: '0 0 20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FileText size={20} style={{ color: accentPrimary }} /> {t('uh_compliance')}
            </h2>

            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>{t('uh_marked')} *</label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {UTILITY_HIT_CONFIG.markingOptions.map(opt => (
                  <button key={opt} onClick={() => setMarkedProperly(opt)} style={{
                    padding: '8px 16px', borderRadius: '8px', cursor: 'pointer',
                    border: `1px solid ${markedProperly === opt ? accentPrimary : borderColor}`,
                    backgroundColor: markedProperly === opt ? accentPrimary + '20' : 'transparent',
                    color: markedProperly === opt ? accentPrimary : textColor,
                    fontSize: '0.9rem', fontWeight: markedProperly === opt ? '600' : '400',
                  }}>
                    {opt}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>{t('uh_locates')} *</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                {[t('ss_yes'), t('ss_no')].map((label, i) => {
                  const val = i === 0 ? 'Yes' : 'No';
                  return (
                    <button key={val} onClick={() => setLocatesCalled(val)} style={{
                      padding: '8px 24px', borderRadius: '8px', cursor: 'pointer',
                      border: `1px solid ${locatesCalled === val ? accentPrimary : borderColor}`,
                      backgroundColor: locatesCalled === val ? accentPrimary + '20' : 'transparent',
                      color: locatesCalled === val ? accentPrimary : textColor,
                      fontSize: '0.9rem', fontWeight: locatesCalled === val ? '600' : '400',
                    }}>
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            {locatesCalled === 'Yes' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>{t('uh_ticket')} *</label>
                <input type="text" value={locateTicket} onChange={(e) => setLocateTicket(e.target.value)} placeholder="Ticket #" style={inputStyle} />
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={labelStyle}>{t('uh_crew_lead')}</label>
                <input type="text" value={crewLeadName} onChange={(e) => setCrewLeadName(e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>{t('uh_crew_phone')}</label>
                <input type="tel" value={crewLeadPhone} onChange={(e) => setCrewLeadPhone(e.target.value)} placeholder="(555) 555-5555" style={inputStyle} />
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>{t('uh_equipment')}</label>
              <input type="text" value={equipmentUsed} onChange={(e) => setEquipmentUsed(e.target.value)} placeholder={lang === 'es' ? 'Ej: Ditch Witch JT20' : 'e.g. Ditch Witch JT20, backhoe'} style={inputStyle} />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>{t('uh_actions')}</label>
              <textarea value={immediateActions} onChange={(e) => setImmediateActions(e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
              <div>
                <label style={labelStyle}>{t('uh_rep_name')}</label>
                <input type="text" value={utilityRepName} onChange={(e) => setUtilityRepName(e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>{t('uh_rep_contact')}</label>
                <input type="text" value={utilityRepContact} onChange={(e) => setUtilityRepContact(e.target.value)} style={inputStyle} />
              </div>
            </div>
          </div>
        )}

        {/* STEP 4 — Photos */}
        {step === 4 && (
          <div style={{ backgroundColor: cardBg, borderRadius: '12px', padding: '24px', border: `1px solid ${borderColor}` }}>
            <h2 style={{ color: textColor, fontSize: '1.1rem', fontWeight: '700', margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Camera size={20} style={{ color: accentPrimary }} /> {t('uh_photos')}
            </h2>
            <p style={{ color: mutedColor, fontSize: '0.85rem', margin: '0 0 20px' }}>
              {t('uh_photos_desc')}
            </p>

            {renderPhotoSection(t('uh_pre_photos'), photosPre, setPhotosPre, false)}
            {renderPhotoSection(t('uh_damage_photos'), photosDamage, setPhotosDamage, true)}
            {renderPhotoSection(t('uh_repair_photos'), photosRepair, setPhotosRepair, false)}
          </div>
        )}

        {/* STEP 5 — Review & Submit */}
        {step === 5 && (
          <div style={{ backgroundColor: cardBg, borderRadius: '12px', padding: '24px', border: `1px solid ${borderColor}` }}>
            <h2 style={{ color: textColor, fontSize: '1.1rem', fontWeight: '700', margin: '0 0 20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CheckCircle size={20} style={{ color: accentSecondary }} /> {t('uh_review')}
            </h2>

            {[
              { title: t('uh_incident'), items: [
                [t('uh_date_time'), new Date(incidentDate).toLocaleString()],
                [t('uh_address'), address],
                ['GPS', gpsLat ? `${gpsLat.toFixed(5)}, ${gpsLng.toFixed(5)}` : 'N/A'],
                [t('uh_utility_type'), utilityType],
                [t('uh_utility_owner'), utilityOwner || 'N/A'],
                [t('uh_severity'), severity],
              ]},
              { title: t('uh_description'), items: [['', description]] },
              { title: t('uh_compliance'), items: [
                [t('uh_marked'), markedProperly],
                [t('uh_locates'), locatesCalled],
                [t('uh_ticket'), locateTicket || 'N/A'],
              ]},
              { title: t('uh_photos'), items: [
                [t('uh_pre_photos'), `${photosPre.length}`],
                [t('uh_damage_photos'), `${photosDamage.length}`],
                [t('uh_repair_photos'), `${photosRepair.length}`],
              ]},
            ].map((section) => (
              <div key={section.title} style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '0.75rem', color: accentPrimary, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
                  {section.title}
                </div>
                {section.items.map(([label, value], i) => (
                  <div key={i} style={{ display: 'flex', gap: '8px', padding: '6px 0', borderBottom: `1px solid ${borderColor}30` }}>
                    {label && <span style={{ fontSize: '0.85rem', color: mutedColor, minWidth: '120px', flexShrink: 0 }}>{label}:</span>}
                    <span style={{ fontSize: '0.85rem', color: textColor, fontWeight: label ? '500' : '400' }}>{value}</span>
                  </div>
                ))}
              </div>
            ))}

            <div style={{ marginTop: '8px', padding: '12px', backgroundColor: darkMode ? '#1a2332' : '#f1f5f9', borderRadius: '8px', fontSize: '0.8rem', color: mutedColor }}>
              {t('uh_reported_by')}: {loggedInUser?.name || 'Unknown'} ({loggedInUser?.email || 'no email'})
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', flexWrap: 'wrap' }}>
              <button onClick={handlePreviewPdf} style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '12px 24px', backgroundColor: 'transparent', color: accentPrimary,
                border: `1px solid ${accentPrimary}`, borderRadius: '8px', cursor: 'pointer', fontSize: '0.95rem',
              }}>
                <Eye size={18} /> {t('uh_preview_pdf')}
              </button>
              <button onClick={handleSubmit} disabled={loading} style={{
                display: 'flex', alignItems: 'center', gap: '8px', flex: 1, justifyContent: 'center',
                padding: '12px 24px', backgroundColor: dangerColor, color: '#fff',
                border: 'none', borderRadius: '8px', cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '1rem', fontWeight: '600', opacity: loading ? 0.7 : 1,
              }}>
                {loading ? <Loader size={18} className="spin" /> : <Send size={18} />}
                {loading ? t('ss_submitting') : t('uh_submit')}
              </button>
            </div>
          </div>
        )}

        {/* Navigation buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px', gap: '12px' }}>
          {step > 1 && step <= 5 && (
            <button onClick={() => setStep(step - 1)} style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '12px 24px', backgroundColor: 'transparent',
              border: `1px solid ${borderColor}`, borderRadius: '8px',
              color: textColor, cursor: 'pointer', fontSize: '0.95rem',
            }}>
              <ArrowLeft size={18} /> {t('uh_back')}
            </button>
          )}
          {step === 1 && <div />}
          {step < 5 && (
            <button
              onClick={() => { if (canProceed()) setStep(step + 1); }}
              disabled={!canProceed()}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto',
                padding: '12px 24px', backgroundColor: canProceed() ? accentPrimary : borderColor,
                color: '#fff', border: 'none', borderRadius: '8px',
                cursor: canProceed() ? 'pointer' : 'not-allowed', fontSize: '0.95rem', fontWeight: '600',
              }}
            >
              {t('uh_next')} <ArrowRight size={18} />
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
        @media (max-width: 600px) {
          div[style*="gridTemplateColumns: repeat(auto-fit"] {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default UtilityHitPage;
